import React, { createContext, useMemo, useState, useCallback } from "react";
import systemFr from "../locales/system-fr.json";
import systemEn from "../locales/system-en.json";
import { createTranslator } from "../core/createTranslator.js";
import { mergeNamespaces } from "../core/mergeNamespaces.js";
import { readStoredLang, writeStoredLang } from "../core/storage.js";

/** @type {import('react').Context<null | { lang: string, setLang: (l: string) => void, t: (key: string, fallback?: string) => string }>} */
const I18nContext = createContext(null);

/**
 * @param {object} props
 * @param {import('react').ReactNode} props.children
 * @param {string} [props.defaultLang]
 * @param {string} [props.storageKey]
 * @param {Record<string, unknown>} [props.appNamespaces]
 * @param {(key: string, lang: string) => void} [props.onMissingKey]
 */
export function I18nProvider({
  children,
  defaultLang = "fr",
  storageKey = "coquille.lang",
  appNamespaces = {},
  onMissingKey,
}) {
  const [lang, setLangState] = useState(() => {
    return readStoredLang(storageKey) || defaultLang;
  });

  const dictionaries = useMemo(() => {
    const fr = mergeNamespaces({ system: systemFr }, appNamespaces.fr || {});
    const en = mergeNamespaces({ system: systemEn }, appNamespaces.en || {});
    return { fr, en };
  }, [appNamespaces]);

  const t = useMemo(
    () => createTranslator(dictionaries, onMissingKey),
    [dictionaries, onMissingKey],
  );

  const setLang = useCallback(
    (next) => {
      setLangState(next);
      writeStoredLang(next, storageKey);
      if (typeof document !== "undefined") {
        document.documentElement.lang = next;
      }
    },
    [storageKey],
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: (key, fallback) => t(`system.${key}`, fallback, lang),
    }),
    [lang, setLang, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export { I18nContext };
